import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { stationVolumeDockAtom, stationVolumeDockHeightAtom } from '@/atoms/stationVolumes.atom';
import type { ClearanceSummary } from '@/lib/clearance/types';
import { formatFixed, staTag } from '@/lib/stationVolumes/format';

export interface ClearanceChipProps {
  layerId: string;
  summary: Pick<ClearanceSummary, 'violationCount' | 'maxIntrusionM' | 'worstStationDisplayM' | 'binsWithViolations'>;
}

/** The panel chip that opens the clearance dock. Seeds the dock height from
 *  the persisted preference, never from the default — opening the dock must
 *  not stomp the user's drag. */
export const ClearanceChip = ({ layerId, summary }: ClearanceChipProps) => {
  const { t } = useTranslation('project');
  const setDock = useSetAtom(stationVolumeDockAtom);
  const dockHeight = useAtomValue(stationVolumeDockHeightAtom);
  return (
    <button
      type='button'
      data-testid='clearance-chip'
      title={t('clearance.chip.open')}
      // The card recipe of the floating stack this chip belongs to —
      // `DeviationColorbar` renders directly above it in the SAME column
      // (`Viewer.tsx`), and the two were drawn with different corner radii,
      // shadows and blurs, so a single stack of cards read as two unrelated
      // widgets. Radius/shadow/blur/background are matched to it exactly; only
      // the border and the hover/focus states are this component's own,
      // because unlike the colorbar this card is a button.
      className='mt-2 flex w-full flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 rounded-lg border border-slate-200/70 bg-white/90 px-3 py-2 text-left shadow-lg backdrop-blur hover:bg-white focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:border-stone-700/70 dark:bg-stone-900/90 dark:hover:bg-stone-900'
      onClick={() => setDock({ layerId, tab: 'chart', height: dockHeight, dataset: 'clearance' })}>
      <span className='font-semibold'>{t('clearance.chip.title')}</span>
      <span className='tabular-nums text-slate-600 dark:text-stone-300'>
        {summary.violationCount === 0
          ? // Finding I12: a clean run says so as a sentence, on the chip that
            // is the only way into the dock — not as a row of zeros.
            t('clearance.chip.clear')
          : // Finding I9: `count` is i18next's magic key, so the bundles carry
            // `_one` / `_other` — without them a single violation read
            // "1 violating points", and "1 verletzende Punkte" in German.
            t('clearance.chip.summary', {
              // `count` still drives i18next's plural selection; `formatted`
              // is what the sentence prints. A raw 8931292 is a digit run
              // nobody can read at a glance — and i18next does not group
              // `count` itself.
              count: summary.violationCount,
              formatted: summary.violationCount.toLocaleString(),
              // Positive, exactly as the dock's KPI prints it: an intrusion
              // DEPTH is a magnitude, and a chip that showed `−2.31` beside a
              // dock showing `2.310` would read as two different figures.
              worst: summary.maxIntrusionM === null ? '—' : formatFixed(summary.maxIntrusionM, 2),
              station: summary.worstStationDisplayM === null ? '—' : staTag(summary.worstStationDisplayM, 0),
            })}
      </span>
    </button>
  );
};
